import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import {
  ActiveTag,
  BottomContent,
  Card,
  CardSection,
  Content,
  Icon,
  LeftContent,
  SessionContent,
  SubHeader,
  TitleSection
} from './styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import Button from '../../../../component/button';
import { getSessionList, removeAllSession, removeSession } from '../../../../services/settingServices';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import DeviceCard from './deviceCard';
import { BUTTON_TYPE, ICON_SIZE } from '../../../../global/constants';
import { SecurityAccessListInterface } from '../../../../interfaces/SettingsInterface';
import { Group, Header, PersonalText, Container } from '../../styles';

// Enable relativeTime plugin for fromNow() functionality
dayjs.extend(relativeTime);

function SecurityAccess() {
  //state
  const [securityAccessList, setSecurityAccessList] = useState<SecurityAccessListInterface[]>([]);

  //other variables
  const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
  const currentUserSessions = securityAccessList?.filter((session) => session.DeviceId === currentUser?.device_id);
  const otherSessions = securityAccessList?.filter((session) => session.DeviceId !== currentUser?.device_id);
  const isMobile = useMobileDevice();

  // Load session list
  const loadData = useCallback(async () => {
    const response = await getSessionList();
    setSecurityAccessList(response || []);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Session revocation handlers
  const handleRevokeSession = useCallback(
    async (sessionId: string) => {
      try {
        const payload = { sessionId };
        const response = await removeSession(payload);
        if (response) {
          const updatedSessions = securityAccessList?.filter((session) => session?.[`_id`] !== sessionId);
          setSecurityAccessList(updatedSessions);
        }
      } catch (error) {
        console.error('Error removing session:', error);
      }
    },
    [securityAccessList]
  );

  // Revoke all sessions
  const handleAllRevokeSession = useCallback(async () => {
    try {
      const response = await removeAllSession();
      if (response) {
        setSecurityAccessList(currentUserSessions);
      }
    } catch (error) {
      console.error('Error removing session:', error);
    }
  }, [currentUserSessions]);

  // Helper function to determine device icon
  const getDeviceIconName = useCallback((deviceType: string) => {
    if (
      deviceType?.startsWith('Mobile') ||
      deviceType?.endsWith('Android OS') ||
      deviceType?.endsWith('iOS') ||
      deviceType?.startsWith('Ipad')
    ) {
      return 'mobile-device-icon';
    } else if (deviceType?.startsWith('Chrome')) {
      return 'chrome-icon';
    } else if (deviceType?.startsWith('Arc')) {
      return 'arc-icon';
    } else if (deviceType?.startsWith('Teamcamp') || deviceType?.startsWith('Tracker')) {
      return 'desktop-icon';
    } else {
      return 'chrome-icon';
    }
  }, []);

  return (
    <>
      <AppLayout isPosition={true}>
        <Container className='desktop-content'>
          {!isMobile ? (
            <Header applyMargin={true}>
              <PersonalText>Security and access</PersonalText>
            </Header>
          ) : (
            <ResponsiveHeader title='Security and access' />
          )}
          <Group>
            <BottomContent>
              <SessionContent>
                <Content>
                  <h2>Sessions</h2>
                  <p>Devices logged into your account</p>
                </Content>
                <Card>
                  <LeftContent>
                    <Icon>
                      <SVGIcon
                        name={getDeviceIconName(currentUserSessions?.[0]?.DeviceType || '')}
                        width={ICON_SIZE.LG}
                        height={ICON_SIZE.LG}
                        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                        fill='var(--text-secondary)'
                      />
                    </Icon>
                    <TitleSection>
                      <h2>{currentUserSessions?.[0]?.DeviceType || 'Unknown Device'}</h2>
                      <p>{currentUserSessions?.[0]?.GeoLocation || ''}</p>
                    </TitleSection>
                  </LeftContent>
                  <ActiveTag>Active</ActiveTag>
                </Card>
              </SessionContent>
              {otherSessions.length > 0 && (
                <SessionContent>
                  <SubHeader>
                    <h2>Other sessions</h2>
                    <Button
                      title='Revoke all'
                      onClick={handleAllRevokeSession}
                      variant={BUTTON_TYPE.SECONDARY}
                      size='small'
                    />
                  </SubHeader>
                  <CardSection>
                    {otherSessions.map((session) => (
                      <DeviceCard
                        key={session.DeviceId}
                        iconName={getDeviceIconName(session.DeviceType) || ''}
                        title={session.DeviceType || 'Unknown Device'}
                        location={session?.GeoLocation || ''}
                        lastSeen={`Last seen ${dayjs(session.LastSeen).fromNow()}`}
                        onRevoke={() => handleRevokeSession(session?.[`_id`])}
                      />
                    ))}
                  </CardSection>
                </SessionContent>
              )}
            </BottomContent>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
}

export default SecurityAccess;
